import { Link } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { cloneDeep } from 'lodash';
import { createContext, CSSProperties, Dispatch, SetStateAction, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';
import useSessionStorageState from 'use-session-storage-state';
import { useIsFeatureEnabled } from '../api/features/features';
import { useGetTaxReturnTOC, useGetWhitePaperReportPreviousVersions } from '../api/report/report';
import { useGetClaimsForCurrentUser } from '../api/user/user';
import { IExecutionStatusStorage } from '../data-types/IExecutionStatusStorage';
import { findPrintReportModalNodeKeyForSourceItem, TaxReturnTOCItemWithRequiredNodeKey } from '../data-types/printReportModalTOCItem';
import { useCancellableViewForm } from '../hooks/useCancellableViewForm';
import { useFeedbackMessages } from '../hooks/useFeedbackMessages';
import { useIndexedDB } from '../hooks/useIndexedDB';
import {
    CalculationExecutionStatusCode,
    DetailedWorkspaceDto,
    IWhitePaperColumn,
    IWhitePaperReport,
    PassthruType,
    Report,
    ReviewFormDataType,
    TaxReturnItemRequest,
    TaxReturnItemType,
    TaxReturnTOC,
    TaxReturnTOCItem,
    TaxReturnTOCKey,
    ViewFormResponse,
    ViewFormResponseFormFields,
} from '../model';
import { FormTocViewActionType, formTocViewReducer, FormTocViewState, initialFormTocViewState } from '../reducers/formTocViewReducer';
import { buildSelectedStatement } from '../utils/DetailedWorkspaceUtils';
import { CommandType } from '../utils/EventGrid.constants';
import { DetailedWorkspacesEnabled, EditFormViewDescriptiveDataEnabled, FormsViewDrilldownEnabled } from '../utils/FeatureFlags.constants';
import {
    adaptReportToIFormCacheRecord,
    DB_FORM_CACHE_NAME,
    getFormCacheStoreName,
    getRecordId,
    IFormCacheRecord,
} from '../utils/FormIndexedDBUtils';
import {
    buildSectionNavigationList,
    getVisibleChildren,
    isGroupedFormPageInvalidForEntity,
    isIntraPdfNavigation,
    isSameLoadedIsolatedItemReNavigation,
    NavigationTarget,
    resolveNavigationTarget,
    resolveNavigationTargetBySectionIndex,
} from '../utils/formNavigation';
import { SUPPRESS_EDITABLE_FIELD_FILL } from '../utils/FormRendering.constants';
import { DEFAULT_FORM_SETTINGS, FormSettings, FORM_ZOOM_SESSION_STORAGE_KEY } from '../utils/FormZoomSettings';
import { DB_VERSION } from '../utils/IndexedDBSchemaRegistry';
import { PdfSyncPhase } from '../utils/pdfViewerSync';
import { tocContainsEntityCodes } from '../utils/PdfViewerUtils';
import {
    areTaxReturnTOCKeysEqual,
    buildEmptyDetailedWorkspaceItemsMap,
    buildTocMaps,
    collectExpandedTocItemIds,
    collectParentTocItemIds,
    collectTocItemIds,
    filterTOCItemsByVisibility,
    findTocItemById,
    findTocItemByIdAndFormPageName,
    getDuplicateTocItemIds,
    getFilteredTOCItemsByCondition,
    getTocItemFormPageName,
    getTocItemFromKeyMap,
    getTocItemKey,
    getTocItemPagesAndParentId,
    getUpdatedCurrentTOCItemStateOnTree,
    isIsolatedTocItem,
    isOverflowStatement,
    resolveTocItemPageIndex,
} from '../utils/TaxReturnTOCUtils';
import { PINNED_SEARCH_PARAM_NAME, TITLE_SEARCH_PARAM_NAME, TOC_SEARCH_PARAM_NAME, VIEW_REPORT_URL } from '../utils/Urls.constant';
import { FetchError } from '../utils/UseCustomInstance';
import { getProcessingReportsStorageKey } from './taxReturnCalculationContext';
import { useCurrentEnterprise } from './useCurrentEnterprise';

export type { FormSettings } from '../utils/FormZoomSettings';
export type { PdfSyncPhase } from '../utils/pdfViewerSync';

export interface ISelectedEntity {
    id: number;
    index: number;
    code: string;
    /**
     * The calc engine's real column id (e.g. 99991 for Total), from the report columns
     * metadata. Present only when the entity was resolved against loaded report columns;
     * the id above is the whitepaper-remapped value (or the URL sentinel) and must not be
     * sent to the engine.
     */
    originalColumnId?: number;
}

export interface LoadTocDataOptions {
    skipGetFormData?: boolean;
    entity?: ISelectedEntity;
    formLoadGeneration?: number;
}

export interface IFormViewerContextProps {
    showForm: boolean;
    tocData: TaxReturnTOC | null | undefined;
    formData: string | null;
    formFields: ViewFormResponseFormFields | null | undefined;
    formDataType: ReviewFormDataType | null | undefined;
    detailedWorkspace: DetailedWorkspaceDto | null | undefined;
    isLoading: boolean;
    isLoadingToc: boolean;
    isLoadingDetailedWorkspace: boolean;
    isFormZoomReady: boolean;
    setFormZoomReady: (ready: boolean) => void;
    loadFormPage: (taxReturnTocItems: TaxReturnTOCItem[], tocKey: TaxReturnTOCKey, entity?: ISelectedEntity | undefined) => void;
    navigateToTocItem: (target: NavigationTarget, entity?: ISelectedEntity | undefined) => void;
    navigateToTocItemFromTree: (item: TaxReturnTOCItem, entity?: ISelectedEntity | undefined) => void;
    navigateToSectionForm: (sectionIndex: number, options?: { goToLastPage?: boolean }) => void;
    navigateToPageNumber: (pageNumber: number) => void;
    navigateToRelativePageNumber: (delta: number) => void;
    navigateToFirstPage: () => void;
    navigateToLastPage: () => void;
    pdfSyncPhase: PdfSyncPhase;
    setPdfSyncPhase: (phase: PdfSyncPhase) => void;
    pdfNavigationRequestId: number;
    loadTocData: (columnIndex?: number, options?: LoadTocDataOptions) => void;
    openForm: (options?: { replace?: boolean }) => void;
    navigateToReport: () => void;
    clearFormData: () => void;
    currentTOCItemState: CurrentTOCItemState | undefined;
    selectedStatement: CurrentTOCItemState | undefined;
    setSelectedStatement: Dispatch<SetStateAction<CurrentTOCItemState | undefined>>;
    onPageChange: (page: any) => any;
    currentFormTotalPages: number;
    pdfViewerLoadedTocItemId: string | null;
    setPdfViewerLoadedTocItemId: (tocItemId: string | null) => void;
    isDrawerPinned: boolean;
    togglePinDrawer: () => void;
    hasTaxReturnTOCItems: boolean;
    formSettings: FormSettings;
    setFormSetting: (key: string, value: any) => void;
    updateFormSettings: (settings: Partial<FormSettings>) => void;
    setNodesExpanded: (
        expanded: boolean,
        node?: TaxReturnTOCItem | undefined,
        tocReferenceMap?: Map<TaxReturnTOCKey, TaxReturnTOCItem>
    ) => void;
    tocTreeState: FormTocViewState;
    setTocSearchText: (searchText: string, options?: { syncUrl?: boolean }) => void;
    expandAllTocNodes: () => void;
    collapseAllTocNodes: () => void;
    setTocNodeExpanded: (nodeId: string, isExpanded: boolean) => void;
    getCurrentFormPageUrl: (taxReturnTOCItemId?: string, formPageName?: string | null) => string;
    setCurrentTOCItemState: Dispatch<SetStateAction<CurrentTOCItemState | undefined>>;
    updateCurrentFormTotalPages: (totalPages: number) => void;
    selectedEntity: ISelectedEntity | undefined;
    returnKey: number;
    currentReport: Report | undefined;
    setCurrentReport: (report: Report | undefined) => void;
    currentWhitePaperReport: IWhitePaperReport | null;
    setCurrentWhitePaperReport: (report: IWhitePaperReport | null) => void;
    hasError: boolean;
    hasAbortedOrTimedOutError: boolean;
    cancelCurrentFormLoad: () => void;
    pendingOverflowPrintSelection: TaxReturnTOCItemWithRequiredNodeKey[] | undefined;
    requestOverflowPrint: (selection: TaxReturnTOCItemWithRequiredNodeKey[] | undefined) => void;
    clearOverflowPrintRequest: () => void;
    isTocItemPrinting: (returnKey: number, tocItemKey: TaxReturnTOCKey | null | undefined) => boolean;
    loadTocAndFormForEntity: (selectedEntity: IWhitePaperColumn | undefined) => void;
    tocReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>;
    dataNameToTocItemMap: Map<string, TaxReturnTOCItem>;
    emptyDetailedWorkspaceItemsMap: Map<string, TaxReturnTOCItem>;
    setDataNameToTocItemMap: (dataNameToTocItemMap: Map<string, TaxReturnTOCItem>) => void;
    showStatement: { visible: boolean; isAdd: boolean };
    setShowStatement: (show: { visible: boolean; isAdd: boolean }) => void;
    loadStatementDataForModal: (
        taxReturnTOCItems: TaxReturnTOCItem[],
        entity?: ISelectedEntity,
        elementIdToFocus?: string | null | undefined,
        isAdd?: boolean
    ) => Promise<void>;
    elementToFocusAfterFormModal: string | undefined;
    setElementToFocusAfterFormModal: (formFieldElement: string | undefined) => void;
    openAddStatementModal: (elementToFocus: string) => void;
    /**
     * Bumped by ReportLinesTable whenever report comment metadata (totalComments/
     * totalUnresolvedComments on report cells) refreshes. currentWhitePaperReport's cells are
     * mutated in place when that happens (see DataManagerUtils), so its object reference never
     * changes and can't be used as an effect dependency on its own — this counter is the signal
     * the PDF form viewer's comment indicators key their refresh off of.
     */
    commentMetadataVersion: number;
    setCommentMetadataVersion: Dispatch<SetStateAction<number>>;
}

export const FormViewContext = createContext<IFormViewerContextProps | null>(null);

interface IFormProviderProps {
    children: any;
    returnKey: number;
    tocItemId?: string | undefined;
    value?: IFormViewerContextProps;
    initialTocData?: TaxReturnTOC | null | undefined;
    initialTocReferenceMap?: Map<TaxReturnTOCKey, TaxReturnTOCItem>;
}

export interface CurrentTOCItemState {
    id: string;
    parentId?: string | null;
    tocKey?: TaxReturnTOCKey | null;
    pageIndex?: number | null;
    entity?: ISelectedEntity | undefined;
    tocItemTitle?: string | null;
    defaultTocItem?: TaxReturnTOCItem;
    //TODO: this might be safe to delete. most enumerated page handling is on the backend now
    isEnumerated?: boolean;
    dataName?: string | null;
    canSelectEntity: boolean;
    taxReturnItemType?: TaxReturnItemType | null;
    isConsolidated?: boolean;
}

export interface IFormPageCache {
    formPageId: string | undefined;
    formPageName: string | null | undefined;
    //TODO: this might be safe to delete. most enumerated page handling is on the backend now
    isEnumerated?: boolean;
}

const overflowWarningLinkStyle: CSSProperties = {
    fontWeight: 'bold',
    textDecoration: 'underline',
    cursor: 'pointer',
};

interface GetBlobDataOptions {
    selectedTocItemId?: string;
    selectionTocData?: TaxReturnTOC | null | undefined;
    selectionReferenceMap?: Map<TaxReturnTOCKey, TaxReturnTOCItem>;
    isStale?: () => boolean;
}

interface InitializeTocStateOptions {
    formLoadGeneration?: number;
}

type ViewFormLoadFailureType = 'overflow-timeout' | 'abort' | 'error';

const getViewFormLoadFailureType = (
    error: unknown,
    taxReturnTOCItems: TaxReturnTOCItem[] | null | undefined,
    selectedTocItemId?: string
): ViewFormLoadFailureType => {
    const fetchError = error as FetchError;
    const isAbortError = fetchError?.name === 'AbortError';
    const is504Error = fetchError?.response?.status === 504;

    if (!taxReturnTOCItems || taxReturnTOCItems.length === 0) {
        return isAbortError ? 'abort' : 'error';
    }

    const selectedItem: TaxReturnTOCItem | undefined = selectedTocItemId
        ? taxReturnTOCItems.find((item: TaxReturnTOCItem) => item.id === selectedTocItemId)
        : undefined;
    const isOverflow: boolean = selectedItem ? isOverflowStatement([selectedItem]) : isOverflowStatement(taxReturnTOCItems);

    if ((isAbortError || is504Error) && isOverflow) {
        return 'overflow-timeout';
    }

    if (isAbortError) {
        return 'abort';
    }

    return 'error';
};

export const getSelectedEntityFromPath = (searchParams: URLSearchParams): ISelectedEntity | undefined => {
    const id = searchParams.get('entity');
    const index = searchParams.get('index');
    const code = searchParams.get('code');

    if (id !== null && index !== null) {
        return {
            id: parseInt(id),
            index: parseInt(index),
            code: code || '',
        };
    }
    return undefined;
};

const getSelectedEntityFromColumn = (column: IWhitePaperColumn | null | undefined, fallbackIndex?: number): ISelectedEntity | undefined => {
    const columnId: number | undefined = column?.columnId;
    const columnIndex: number | undefined = column?.originalColumnIndex ?? column?.columnIndex ?? fallbackIndex;

    if (!Number.isInteger(columnId) || !Number.isInteger(columnIndex)) {
        return undefined;
    }

    return {
        id: columnId ?? 0,
        index: columnIndex ?? 0,
        code: column?.entityCode ?? '',
        originalColumnId: column?.originalColumnId,
    };
};

const getTocKeyByItemId = (
    referenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem> | undefined,
    itemId: string | null | undefined
): TaxReturnTOCKey | undefined => {
    if (!referenceMap || !itemId) {
        return undefined;
    }

    for (const [key, item] of referenceMap.entries()) {
        if (item.id === itemId) {
            return key;
        }
    }

    return undefined;
};

const doesSelectedEntityMatchColumn = (selectedEntity: ISelectedEntity, column: IWhitePaperColumn, fallbackIndex?: number): boolean => {
    const columnEntity: ISelectedEntity | undefined = getSelectedEntityFromColumn(column, fallbackIndex);

    if (!columnEntity) {
        return false;
    }

    const hasSameId: boolean = columnEntity.id === selectedEntity.id;
    const hasSameIndex: boolean = columnEntity.index === selectedEntity.index;
    const hasMatchingCode: boolean = selectedEntity.code.length === 0 || columnEntity.code === selectedEntity.code;

    return hasSameId && hasSameIndex && hasMatchingCode;
};

export const resolveColumnForSelectedEntity = (
    selectedEntity: ISelectedEntity | undefined,
    reportColumns: IWhitePaperColumn[] | null | undefined
): IWhitePaperColumn | undefined => {
    if (!selectedEntity || !reportColumns || reportColumns.length === 0) {
        return undefined;
    }

    const matchingColumnIndex: number = reportColumns.findIndex((column: IWhitePaperColumn, index: number): boolean =>
        doesSelectedEntityMatchColumn(selectedEntity, column, index)
    );

    if (matchingColumnIndex >= 0) {
        return reportColumns[matchingColumnIndex];
    }

    return undefined;
};

export const resolveSelectedEntityForReport = (
    searchParams: URLSearchParams,
    reportColumns: IWhitePaperColumn[] | null | undefined
): ISelectedEntity | undefined => {
    const selectedEntityFromPath: ISelectedEntity | undefined = getSelectedEntityFromPath(searchParams);

    if (!selectedEntityFromPath) {
        return getSelectedEntityFromColumn(reportColumns?.[0], 0);
    }

    if (!reportColumns || reportColumns.length === 0) {
        return selectedEntityFromPath;
    }

    const matchingColumnIndex: number = reportColumns.findIndex((column: IWhitePaperColumn, index: number): boolean => {
        return doesSelectedEntityMatchColumn(selectedEntityFromPath, column, index);
    });

    const fallbackIndex: number = matchingColumnIndex >= 0 ? matchingColumnIndex : 0;

    return getSelectedEntityFromColumn(reportColumns[fallbackIndex], fallbackIndex);
};

const getEffectiveSelectedEntity = (
    searchParams: URLSearchParams,
    currentWhitePaperReport: IWhitePaperReport | null
): ISelectedEntity | undefined => {
    return resolveSelectedEntityForReport(searchParams, currentWhitePaperReport?.columns);
};

const getResolvedSelectedEntity = (
    providedEntity: ISelectedEntity | undefined,
    selectedEntity: ISelectedEntity | undefined,
    currentTOCItemState: CurrentTOCItemState | undefined,
    searchParams: URLSearchParams,
    currentWhitePaperReport: IWhitePaperReport | null
): ISelectedEntity | undefined => {
    return (
        providedEntity ?? selectedEntity ?? currentTOCItemState?.entity ?? getEffectiveSelectedEntity(searchParams, currentWhitePaperReport)
    );
};

export function FormViewProvider({
    children,
    returnKey,
    tocItemId,
    value,
    initialTocData = null,
    initialTocReferenceMap = new Map(),
}: IFormProviderProps) {
    const [showForm, setShowForm] = useState(tocItemId ? true : false);
    const [formData, setFormData] = useState<string | null>(null);
    const [tocData, setTocData] = useState<TaxReturnTOC | null>(initialTocData);
    const [currentWhitePaperReport, setCurrentWhitePaperReport] = useState<IWhitePaperReport | null>(null);
    const [commentMetadataVersion, setCommentMetadataVersion] = useState(0);
    const [formFields, setFormFields] = useState<ViewFormResponseFormFields | null | undefined>(null);
    const [formDataType, setFormDataType] = useState<ReviewFormDataType | null | undefined>(null);
    const [detailedWorkspace, setDetailedWorkspace] = useState<DetailedWorkspaceDto | null | undefined>(null);
    const [currentTOCItemState, setCurrentTOCItemState] = useState<CurrentTOCItemState>();
    const currentTOCItemStateRef = useRef<CurrentTOCItemState | undefined>(undefined);
    currentTOCItemStateRef.current = currentTOCItemState;
    const [selectedStatement, setSelectedStatement] = useState<CurrentTOCItemState | undefined>(undefined);
    const [currentFormTotalPages, setCurrentFormTotalPages] = useState<number>(1);
    const currentFormTotalPagesRef = useRef<number>(1);
    currentFormTotalPagesRef.current = currentFormTotalPages;
    const [formSettings, setFormSettings] = useLocalStorageState<FormSettings>(FORM_ZOOM_SESSION_STORAGE_KEY, {
        defaultValue: DEFAULT_FORM_SETTINGS,
    });
    const [isFormZoomReady, setIsFormZoomReady] = useState(false);
    const [pdfViewerLoadedTocItemId, setPdfViewerLoadedTocItemIdState] = useState<string | null>(null);
    const pdfViewerLoadedTocItemIdRef = useRef<string | null>(null);
    const setPdfViewerLoadedTocItemId = (tocItemId: string | null): void => {
        pdfViewerLoadedTocItemIdRef.current = tocItemId;
        setPdfViewerLoadedTocItemIdState(tocItemId);
    };
    const [pdfSyncPhase, setPdfSyncPhaseState] = useState<PdfSyncPhase>('idle');
    const pdfSyncPhaseRef = useRef<PdfSyncPhase>('idle');
    const setPdfSyncPhase = (phase: PdfSyncPhase): void => {
        pdfSyncPhaseRef.current = phase;
        setPdfSyncPhaseState(phase);
    };
    const [pdfNavigationRequestId, setPdfNavigationRequestId] = useState<number>(0);
    const requestPdfNavigation = (): void => {
        setPdfNavigationRequestId((previousId) => previousId + 1);
    };
    const activeNavigationGenerationRef = useRef(0);
    const pendingFormLoadGenerationRef = useRef(0);
    const [hasError, setHasError] = useState<boolean>(false);
    const [hasAbortedOrTimedOutError, setHasAbortedOrTimedOutError] = useState<boolean>(false);
    const [pendingOverflowPrintSelection, setPendingOverflowPrintSelection] = useState<TaxReturnTOCItemWithRequiredNodeKey[] | undefined>(
        undefined
    );
    const navigate = useNavigate();
    const location = useLocation();
    let searchParams = new URLSearchParams(location.search);
    const isDrawerPinned = searchParams.get(PINNED_SEARCH_PARAM_NAME) === 'true';
    const initializeTocViewState = (initialState: FormTocViewState): FormTocViewState => {
        const searchText: string = searchParams.get(TOC_SEARCH_PARAM_NAME) ?? '';
        const expandedIds: string[] = collectExpandedTocItemIds(initialTocData?.items);

        return {
            ...initialState,
            expandedIds: new Set(expandedIds),
            searchText,
        };
    };
    const [tocTreeState, dispatchTocTreeState] = useReducer(formTocViewReducer, initialFormTocViewState, initializeTocViewState);
    const hasInitialTocItems: boolean = Boolean(initialTocData?.items && initialTocData.items.length > 0);
    const [hasTaxReturnTOCItems, { setTrue: setHasTaxReturnTOCItemsTrue, setFalse: setHasTaxReturnTOCItemsFalse }] =
        useBoolean(hasInitialTocItems);
    const { t } = useTranslation();
    const { displayFeedbackMessage, clearActiveMessage } = useFeedbackMessages();
    const { data: availableVersions } = useGetWhitePaperReportPreviousVersions({
        ReportId: returnKey,
    });
    const [selectedEntity, setSelectedEntity] = useState<ISelectedEntity | undefined>(undefined);
    const [currentReport, setCurrentReport] = useState<Report | undefined>(undefined);
    const { insertRecord, getRecord, removePreviousVersions, deleteRecord } = useIndexedDB<IFormCacheRecord>(window.indexedDB, DB_VERSION);

    const { data: userClaims } = useGetClaimsForCurrentUser();
    const { enterprise } = useCurrentEnterprise();

    const processingReportsStorageKey = getProcessingReportsStorageKey(userClaims?.environmentId || '', userClaims?.userId || '');
    const [isProcessingReportsStorageValue] = useSessionStorageState(processingReportsStorageKey, {
        defaultValue: [] as IExecutionStatusStorage[],
    });

    const storeName = useMemo(() => {
        return getFormCacheStoreName(userClaims) || '';
    }, [userClaims]);
    const [tocReferenceMap, setTocReferenceMap] = useState<Map<TaxReturnTOCKey, TaxReturnTOCItem>>(initialTocReferenceMap);
    const [dataNameToTocItemMap, setDataNameToTocItemMap] = useState<Map<string, TaxReturnTOCItem>>(new Map());
    const [emptyDetailedWorkspaceItemsMap, setEmptyDetailedWorkspaceItemsMap] = useState<Map<string, TaxReturnTOCItem>>(new Map());
    const [showStatement, setShowStatement] = useState({ visible: false, isAdd: false });
    const { data: isDetailedWorkspacesEnabled } = useIsFeatureEnabled({
        featureFlagName: DetailedWorkspacesEnabled,
    });
    const { data: isFormsViewDrilldownEnabled, isFetched: isFormsViewDrilldownFlagFetched } = useIsFeatureEnabled({
        featureFlagName: FormsViewDrilldownEnabled,
    });
    const { data: isEditFormViewDescriptiveDataEnabled, isFetched: isEditFormViewDescriptiveDataFlagFetched } = useIsFeatureEnabled({
        featureFlagName: EditFormViewDescriptiveDataEnabled,
    });

    /**
     * ViewForm responses bake field styling (interactive borders) from the row sets and
     * feature flags on the request, so a request sent before the whitepaper report and
     * both flag queries resolve would fetch — and cache — a permanently unstyled form.
     * Form loads that miss the cache are parked here until these inputs are ready.
     */
    const areFormStyleInputsReady: boolean =
        !SUPPRESS_EDITABLE_FIELD_FILL ||
        (currentWhitePaperReport !== null && isFormsViewDrilldownFlagFetched && isEditFormViewDescriptiveDataFlagFetched);
    const pendingStyledFormLoadRef = useRef<{
        taxReturnTOCItems: TaxReturnTOCItem[];
        entity: ISelectedEntity | undefined;
        options?: GetBlobDataOptions;
    } | null>(null);
    // Parking happens inside an async chain that may hold a stale closure (the inputs can
    // have become ready after the chain started), and a ref write alone does not re-render.
    // Bumping this state forces a render so the replay effect re-evaluates readiness with
    // current values immediately after every park.
    const [, setPendingStyledFormLoadNonce] = useState(0);
    const [elementToFocusAfterFormModal, setElementToFocusAfterFormModal] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (availableVersions && availableVersions.length > 0 && storeName) {
            const versionId = availableVersions[0].versionId ? availableVersions[0].versionId.toString() : '';
            removePreviousVersions(DB_FORM_CACHE_NAME, storeName, versionId, { indexName: 'returnKey', query: returnKey.toString() });
        }
    }, [availableVersions, removePreviousVersions, storeName]);

    const {
        mutateAsync: viewFormPdfMutation,
        isLoading: isLoadingPdf,
        variables: requestVariables,
        cancel: cancelViewForm,
    } = useCancellableViewForm({
        onSuccess: async (res: ViewFormResponse) => {
            if (pendingFormLoadGenerationRef.current !== activeNavigationGenerationRef.current) {
                return;
            }

            clearActiveMessage();
            if (!res.formContent && !res.detailedWorkspace) {
                return;
            }
            if (res.formFields) {
                setFormFields(res.formFields);
            }
            if (res.detailedWorkspace && !selectedStatement) {
                setDetailedWorkspace(res.detailedWorkspace);
            }
            const isDetailedWorkspace = requestVariables?.taxReturnTOCItems?.some((item) => item.isDetailedWorkspace);
            if (!isDetailedWorkspace) {
                const formCacheRecord = adaptReportToIFormCacheRecord(
                    returnKey.toString(),
                    res.formContent ?? '',
                    enterprise?.enterpriseId,
                    requestVariables,
                    availableVersions?.[0]?.versionId,
                    res.formFields,
                    res.dataType,
                    res.detailedWorkspace
                );
                if (formCacheRecord && storeName) {
                    insertRecord(DB_FORM_CACHE_NAME, storeName, formCacheRecord);
                }
            }
            setFormDataType(res.dataType);
            setFormData(res.formContent ?? null);

            if (res.dataType === ReviewFormDataType.Html) {
                setCurrentFormTotalPages(1);
                setPdfSyncPhase('idle');
            }
        },
        onError: (error) => {
            clearActiveMessage();
            const failureType: ViewFormLoadFailureType = getViewFormLoadFailureType(
                error,
                requestVariables?.taxReturnTOCItems,
                currentTOCItemStateRef.current?.id
            );

            if (failureType === 'overflow-timeout') {
                setHasAbortedOrTimedOutError(true);
                return;
            }

            if (failureType === 'abort') {
                return;
            }

            setHasError(true);
        },
    });

    const { mutateAsync: viewFormModalMutation, isLoading: isLoadingDetailedWorkspace } = useCancellableViewForm();

    // Check if a specific TOC item is currently being printed
    const isTocItemPrinting = (returnKey: number, tocItemKey: TaxReturnTOCKey | null | undefined): boolean => {
        if (!tocItemKey) {
            return false;
        }

        return isProcessingReportsStorageValue.some(
            (item: IExecutionStatusStorage) =>
                item?.taxReturnItemKey === returnKey &&
                areTaxReturnTOCKeysEqual(item?.tocItemKey, tocItemKey) &&
                item.statusCode === CalculationExecutionStatusCode.Executing &&
                item.commandType === CommandType.Print
        );
    };

    const handleError = (errorMessage?: string) => {
        if (showForm) {
            displayFeedbackMessage({
                type: 'error',
                message: errorMessage ? `${t('pdfDisplayError')}: ${errorMessage}` : t('pdfDisplayError'),
            });
        }
    };

    const clearFormData = () => {
        setShowForm(false);
        setFormData(null);
        setFormDataType(null);
        setIsFormZoomReady(false);
        setTocData(null);
        setEmptyDetailedWorkspaceItemsMap(new Map());
        setFormFields(null);
        setDetailedWorkspace(null);
        setCurrentTOCItemState(undefined);
        setPdfViewerLoadedTocItemId(null);
        setPdfSyncPhase('idle');
        setHasError(false);
        setHasAbortedOrTimedOutError(false);
        setPendingOverflowPrintSelection(undefined);
        setSelectedEntity(undefined);
    };

    useEffect(() => {
        const formRouteRegex = new RegExp(`^${VIEW_REPORT_URL}\\d+/forms/?(.*)$`);
        const isFormRoute = formRouteRegex.test(location.pathname);

        if (isFormRoute) {
            setShowForm(true);
        } else {
            setShowForm(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (currentTOCItemState?.id && showForm) {
            navigateToPage(
                currentTOCItemState.id,
                currentTOCItemState.entity,
                currentTOCItemState.tocKey?.formPageName ?? currentTOCItemState.tocItemTitle
            );
        }
    }, [
        currentTOCItemState?.id,
        currentTOCItemState?.entity,
        currentTOCItemState?.tocItemTitle,
        currentTOCItemState?.tocKey?.formPageName,
        showForm,
    ]);

    const duplicateTocItemIds: Set<string> = useMemo(() => getDuplicateTocItemIds(tocData?.items), [tocData]);

    const getCurrentFormPageUrl = (taxReturnTOCItemId?: string, formPageName?: string | null): string => {
        const resolvedId: string | undefined =
            taxReturnTOCItemId ?? currentTOCItemState?.id ?? currentTOCItemState?.defaultTocItem?.id ?? undefined;

        if (!resolvedId) {
            return `${VIEW_REPORT_URL}${returnKey}/forms`;
        }

        const baseUrl: string = `${VIEW_REPORT_URL}${returnKey}/forms/${resolvedId}`;
        const resolvedFormPageName: string =
            formPageName ??
            (taxReturnTOCItemId ? '' : currentTOCItemState?.tocKey?.formPageName ?? currentTOCItemState?.tocItemTitle ?? '');

        if (resolvedFormPageName && duplicateTocItemIds.has(resolvedId)) {
            const params = new URLSearchParams();
            params.set(TITLE_SEARCH_PARAM_NAME, resolvedFormPageName);
            return `${baseUrl}?${params.toString()}`;
        }

        return baseUrl;
    };

    const navigateToPage = (id: string, entity?: ISelectedEntity | undefined, formPageName?: string | null) => {
        const selectedEntity = entity?.id ? entity : currentTOCItemState?.entity;

        if (selectedEntity && Number.isInteger(selectedEntity?.id)) {
            searchParams.set('entity', selectedEntity.id.toString());
        }
        if (selectedEntity && Number.isInteger(selectedEntity?.index)) {
            searchParams.set('index', selectedEntity.index?.toString());
        }
        if (selectedEntity && selectedEntity.code) {
            searchParams.set('code', selectedEntity.code);
        }

        if (formPageName && duplicateTocItemIds.has(id)) {
            searchParams.set(TITLE_SEARCH_PARAM_NAME, formPageName);
        } else {
            searchParams.delete(TITLE_SEARCH_PARAM_NAME);
        }

        // This also runs when the TOC item resolves on initial load (currentTOCItemState.id
        // going from undefined to a value on mount/refresh) - rebuilding the pathname from
        // scratch would strip a /comments or /cell/:columnId/:rowId/comments suffix already in
        // the URL before useIsCommentsSubroute/useCellCommentsRouteDetails ever see it, so a
        // refresh or deep link into a forms comments URL would never open the panel. Preserve
        // that suffix when we're re-syncing the URL for the SAME toc item; a genuine navigation
        // to a different item still drops it.
        const formsPagePrefix = `${VIEW_REPORT_URL}${returnKey}/forms/${id}`;
        const currentPathSuffix = location.pathname.startsWith(formsPagePrefix) ? location.pathname.slice(formsPagePrefix.length) : '';
        const commentsSuffix = /^\/(cell\/[^/]+\/[^/]+\/)?comments(\/.*)?$/.test(currentPathSuffix) ? currentPathSuffix : '';

        navigate({ pathname: `${formsPagePrefix}${commentsSuffix}`, search: searchParams.toString() }, { replace: true });
    };

    const openForm = (options?: { replace?: boolean }) => {
        searchParams = new URLSearchParams();

        if (currentTOCItemState?.id && currentTOCItemState.id.length > 0) {
            navigateToPage(currentTOCItemState.id, currentTOCItemState.entity);
        } else if (currentTOCItemState?.defaultTocItem?.id) {
            if (!formData || hasError) {
                loadTocData();
            }
            updateCurrentPage(currentTOCItemState.defaultTocItem);
            navigateToPage(currentTOCItemState.defaultTocItem.id);
        } else {
            navigate(`${VIEW_REPORT_URL}${returnKey}/forms`, { replace: options?.replace ?? false });
        }
    };

    const openStatementModal = (taxReturnTOCItems: TaxReturnTOCItem[], elementIdToFocus?: string, isAdd?: boolean) => {
        if (taxReturnTOCItems.some((item) => item.isStatement && item.isDetailedWorkspace)) {
            setShowStatement({ visible: true, isAdd: isAdd ?? showStatement.isAdd });
            setElementToFocusAfterFormModal(elementIdToFocus ?? `tree-item-1-${taxReturnTOCItems[0].id}`);
        }
    };

    const openAddStatementModal = (elementToFocus: string): void => {
        setShowStatement({ visible: true, isAdd: true });
        setDetailedWorkspace(null);
        setSelectedStatement(undefined);
        setElementToFocusAfterFormModal(elementToFocus);
    };

    const checkCachedDataTypeMismatch = (
        firstTaxReturnTOCItem: TaxReturnTOCItem | undefined,
        result: IFormCacheRecord | undefined,
        isDetailedWorkspacesEnabled: boolean | undefined
    ): boolean => {
        const isDetailedWorkspace = firstTaxReturnTOCItem?.isDetailedWorkspace ?? false;
        const expectedDataType = isDetailedWorkspace
            ? isDetailedWorkspacesEnabled
                ? ReviewFormDataType.Table
                : ReviewFormDataType.Html
            : null;
        const cachedDataTypeMismatch = expectedDataType !== null && result?.dataType !== expectedDataType;

        return cachedDataTypeMismatch;
    };

    const requestOverflowPrint = (selection: TaxReturnTOCItemWithRequiredNodeKey[] | undefined): void => {
        setPendingOverflowPrintSelection(selection ?? []);
    };

    const clearOverflowPrintRequest = (): void => {
        setPendingOverflowPrintSelection(undefined);
    };

    const getOverflowPrintSelectionSourceItem = (
        taxReturnTOCItems: TaxReturnTOCItem[],
        options?: GetBlobDataOptions
    ): TaxReturnTOCItem | undefined => {
        const selectedTocItemId: string | undefined = options?.selectedTocItemId;
        const selectionTocItems: TaxReturnTOCItem[] | null | undefined = options?.selectionTocData?.items ?? tocData?.items;

        if (selectedTocItemId) {
            const sourceItemFromTree: TaxReturnTOCItem | undefined = findTocItemById(selectionTocItems, selectedTocItemId);
            if (sourceItemFromTree) {
                return sourceItemFromTree;
            }

            const sourceItemFromRequestedItems: TaxReturnTOCItem | undefined = taxReturnTOCItems.find(
                (item: TaxReturnTOCItem): boolean => item.id === selectedTocItemId
            );

            if (sourceItemFromRequestedItems) {
                return sourceItemFromRequestedItems;
            }
        }

        return taxReturnTOCItems[0];
    };

    const buildOverflowPrintSelection = (
        taxReturnTOCItems: TaxReturnTOCItem[],
        options?: GetBlobDataOptions
    ): TaxReturnTOCItemWithRequiredNodeKey[] | undefined => {
        const selectionSourceItem: TaxReturnTOCItem | undefined = getOverflowPrintSelectionSourceItem(taxReturnTOCItems, options);
        const selectionTocData: TaxReturnTOC | null | undefined = options?.selectionTocData ?? tocData;

        if (!selectionSourceItem || !selectionTocData?.items) {
            return undefined;
        }

        const nodeKey: string | undefined = findPrintReportModalNodeKeyForSourceItem(selectionTocData.items, selectionSourceItem);

        if (!nodeKey) {
            return undefined;
        }

        return [
            {
                ...selectionSourceItem,
                nodeKey,
            },
        ];
    };

    const isSelectedOverflowStatement = (taxReturnTOCItems: TaxReturnTOCItem[], options?: GetBlobDataOptions): boolean => {
        const selectionSourceItem: TaxReturnTOCItem | undefined = getOverflowPrintSelectionSourceItem(taxReturnTOCItems, options);

        if (!selectionSourceItem) {
            return isOverflowStatement(taxReturnTOCItems);
        }

        return isOverflowStatement([selectionSourceItem]);
    };

    const handleOverflowDownloadRequest = (taxReturnTOCItems: TaxReturnTOCItem[], options?: GetBlobDataOptions): void => {
        activeNavigationGenerationRef.current += 1;
        cancelViewForm();
        clearActiveMessage();
        requestOverflowPrint(buildOverflowPrintSelection(taxReturnTOCItems, options));
    };

    const cancelCurrentFormLoad = (): void => {
        cancelViewForm();
        setHasAbortedOrTimedOutError(true);
        clearActiveMessage();
    };

    const getBlobData = async (
        taxReturnTOCItems: TaxReturnTOCItem[],
        entity: ISelectedEntity | undefined,
        options?: GetBlobDataOptions
    ) => {
        try {
            if (!taxReturnTOCItems || taxReturnTOCItems.length === 0) {
                throw new Error(t('noFormsPagesFound').toString());
            }

            const firstTaxReturnTOCItem = taxReturnTOCItems[0];

            if (!firstTaxReturnTOCItem || !firstTaxReturnTOCItem.id) {
                throw new Error(t('noFormPageIdOrNameFound').toString());
            }

            const columnId = entity?.id.toString();
            const columnIndex = entity?.index.toString();

            const id = getRecordId(
                firstTaxReturnTOCItem.id,
                returnKey.toString(),
                enterprise?.enterpriseId,
                availableVersions?.[0]?.versionId,
                columnId,
                columnIndex
            );

            const result: IFormCacheRecord | undefined = storeName ? await getRecord(DB_FORM_CACHE_NAME, storeName, id) : undefined;

            const cachedDataTypeMismatch: boolean = checkCachedDataTypeMismatch(firstTaxReturnTOCItem, result, isDetailedWorkspacesEnabled);
            const selectedTocItemId: string | undefined = options?.selectedTocItemId ?? firstTaxReturnTOCItem.id;
            const selectionReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem> | undefined =
                options?.selectionReferenceMap ?? tocReferenceMap;
            const selectedTocItemKey: TaxReturnTOCKey | undefined = getTocKeyByItemId(selectionReferenceMap, selectedTocItemId);
            const shouldShowOverflowWarning: boolean = isSelectedOverflowStatement(taxReturnTOCItems, options);
            const isSelectedOverflowStatementPrinting: boolean =
                Boolean(selectedTocItemId) && shouldShowOverflowWarning && isTocItemPrinting(returnKey, selectedTocItemKey);

            if (cachedDataTypeMismatch && result && storeName) {
                await deleteRecord(DB_FORM_CACHE_NAME, storeName, id);
            }

            if (!result || cachedDataTypeMismatch) {
                if (shouldShowOverflowWarning && !isSelectedOverflowStatementPrinting) {
                    displayFeedbackMessage({
                        type: 'warning',
                        message: (
                            <span>
                                <Trans
                                    i18nKey='loadingOverflowWarning'
                                    components={{
                                        l: (
                                            <Link
                                                as='button'
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    handleOverflowDownloadRequest(taxReturnTOCItems, options);
                                                }}
                                                style={overflowWarningLinkStyle}
                                            />
                                        ),
                                    }}
                                />
                            </span>
                        ),
                    });
                } else {
                    clearActiveMessage();
                }

                if (entity && entity.id > 0) {
                    taxReturnTOCItems = getFilteredTOCItemsByCondition(taxReturnTOCItems, (item) => !item.isEnumerated);
                }

                setIsFormZoomReady(false);

                if (options?.isStale?.()) {
                    return;
                }

                if (!areFormStyleInputsReady) {
                    pendingStyledFormLoadRef.current = { taxReturnTOCItems, entity, options };
                    setPendingStyledFormLoadNonce((nonce) => nonce + 1);
                    return;
                }

                await viewFormPdfMutation(
                    getFormRequestData(taxReturnTOCItems, entity, firstTaxReturnTOCItem.taxReturnTOCKey?.taxReturnItemType)
                );
                openStatementModal(taxReturnTOCItems);
                return;
            }

            clearActiveMessage();

            if (options?.isStale?.()) {
                return;
            }

            setFormData(result.formData);
            setFormDataType(result.dataType);
            setFormFields(result.formFields || null);
            if (result.detailedWorkspace) {
                setDetailedWorkspace(result.detailedWorkspace);
            }

            if (result.dataType === ReviewFormDataType.Html) {
                setCurrentFormTotalPages(1);
                setPdfSyncPhase('idle');
            }

            openStatementModal(taxReturnTOCItems);
        } catch (error) {
            clearActiveMessage();
            if (options?.isStale?.()) {
                return;
            }

            const failureType: ViewFormLoadFailureType = getViewFormLoadFailureType(error, taxReturnTOCItems, options?.selectedTocItemId);

            if (failureType === 'overflow-timeout') {
                setFormData(null);
                setFormDataType(null);
                setHasAbortedOrTimedOutError(true);
                return;
            }

            if (failureType === 'abort') {
                return;
            }

            setFormData(null);
            setFormDataType(null);
            setHasError(true);
        }
    };

    // Replays a form load parked by getBlobData once the whitepaper report and flag
    // queries have resolved. Runs without a dependency array so the replay always uses
    // the latest closure; the guards make the common pass a no-op. Latest-wins: a newer
    // navigation that parks while unready overwrites the previous pending load, and
    // stale replays are still dropped by the options.isStale generation check inside
    // getBlobData.
    useEffect(() => {
        if (!areFormStyleInputsReady || !pendingStyledFormLoadRef.current) {
            return;
        }
        const pending = pendingStyledFormLoadRef.current;
        pendingStyledFormLoadRef.current = null;
        void getBlobData(pending.taxReturnTOCItems, pending.entity, pending.options);
    });

    const navigateToReport = () => {
        navigate(`${VIEW_REPORT_URL}${returnKey}`);
    };

    const loadStatementDataForModal = async (
        taxReturnTOCItems: TaxReturnTOCItem[],
        entity?: ISelectedEntity,
        elementIdToFocus?: string | null | undefined,
        isAdd?: boolean
    ) => {
        try {
            if (!taxReturnTOCItems || taxReturnTOCItems.length === 0) {
                return;
            }

            const firstTaxReturnTOCItem = taxReturnTOCItems[0];

            if (!firstTaxReturnTOCItem || !firstTaxReturnTOCItem.id) {
                return;
            }

            setSelectedStatement(buildSelectedStatement(firstTaxReturnTOCItem, entity));

            let itemsToLoad = taxReturnTOCItems;
            if (entity && entity.id > 0) {
                itemsToLoad = getFilteredTOCItemsByCondition(taxReturnTOCItems, (item) => !item.isEnumerated);
            }

            const response = await viewFormModalMutation(
                getFormRequestData(itemsToLoad, entity, firstTaxReturnTOCItem.taxReturnTOCKey?.taxReturnItemType)
            );

            if (response.detailedWorkspace) {
                setDetailedWorkspace(response.detailedWorkspace);
            }

            openStatementModal(itemsToLoad, elementIdToFocus ?? undefined, isAdd);
        } catch (err) {
            console.error('Error loading statement data for modal: ', err);
        }
    };

    const findItemIdToExpand = (
        defaultItemId: string | undefined,
        referenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>
    ): string | undefined => {
        // Try to find by search title if present on URL
        const searchTitle: string | null = searchParams.get(TITLE_SEARCH_PARAM_NAME);
        if (searchTitle) {
            const foundById: string | undefined = Array.from(referenceMap.values()).find(
                (item: TaxReturnTOCItem) => getTocItemFormPageName(item) === searchTitle || item.title === searchTitle
            )?.id;
            if (foundById) {
                return foundById;
            }
        }

        // Try default item ID
        const defaultItemExists: boolean = Boolean(
            defaultItemId && Array.from(referenceMap.values()).some((item: TaxReturnTOCItem) => item.id === defaultItemId)
        );
        if (defaultItemExists) {
            return defaultItemId;
        }

        // Fallback to first ReportsFormPage item
        const firstFormPageId: string | undefined = Array.from(referenceMap.values()).find(
            (item: TaxReturnTOCItem) => item.taxReturnTOCKey?.taxReturnItemType === TaxReturnItemType.ReportsFormPage
        )?.id;

        // if still fails just open first available statement
        if (!firstFormPageId) {
            return Array.from(referenceMap.values()).find(
                (item: TaxReturnTOCItem) => item.taxReturnTOCKey?.taxReturnItemType === TaxReturnItemType.Statement
            )?.id;
        }

        return firstFormPageId;
    };

    const isTocItemVisible = (tocItems: TaxReturnTOCItem[] | null | undefined, tocItemId: string | undefined): boolean => {
        if (!tocItems || !tocItemId) {
            return false;
        }

        const tocItem: TaxReturnTOCItem | undefined = findTocItemById(tocItems, tocItemId);

        if (!tocItem) {
            return false;
        }

        const isVisibleInTocView: boolean = tocItem.isVisibleInTOCView !== false;

        return isVisibleInTocView;
    };

    const resolveItemIdToExpand = (data: TaxReturnTOC, referenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>): string | undefined => {
        const requestedItemKey: TaxReturnTOCKey | undefined = currentTOCItemState?.tocKey ?? undefined;
        const requestedItemFromKey: TaxReturnTOCItem | undefined = requestedItemKey
            ? getTocItemFromKeyMap(referenceMap, requestedItemKey)
            : undefined;
        const requestedItemId: string | undefined = requestedItemFromKey?.id ?? tocItemId ?? currentTOCItemState?.id;
        const hasVisibleRequestedItem: boolean = requestedItemFromKey
            ? requestedItemFromKey.isVisibleInTOCView !== false
            : isTocItemVisible(data.items, requestedItemId);

        if (requestedItemId && hasVisibleRequestedItem) {
            return requestedItemId;
        }

        if (requestedItemId && !hasVisibleRequestedItem) {
            const defaultItemKey: TaxReturnTOCKey | undefined = getTocItemKey(data.defaultItem ?? undefined);
            const defaultItemFromKey: TaxReturnTOCItem | undefined = defaultItemKey
                ? getTocItemFromKeyMap(referenceMap, defaultItemKey)
                : undefined;
            const defaultItemId: string | undefined = defaultItemFromKey?.id ?? data.defaultItem?.id;
            if (defaultItemId) {
                return defaultItemId;
            }
        }

        return findItemIdToExpand(data.defaultItem?.id, referenceMap);
    };

    const initializeTocTreeState = (tocItems: TaxReturnTOCItem[] | null | undefined, preserveExpanded: boolean): void => {
        const expandedIds: string[] = collectExpandedTocItemIds(tocItems);
        const expandedIdSet: Set<string> = new Set(expandedIds);

        dispatchTocTreeState({
            type: FormTocViewActionType.Initialize,
            expandedIds: expandedIdSet,
            preserveExpanded,
        });
    };

    const initializeTocState = (
        data: TaxReturnTOC,
        referenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>,
        itemIdToExpand: string | undefined,
        entity: ISelectedEntity | undefined,
        skipGetFormData?: boolean,
        options?: InitializeTocStateOptions
    ) => {
        const previousTocItemState = currentTOCItemStateRef.current;

        if (previousTocItemState) {
            const itemMissing = Boolean(itemIdToExpand && !findTocItemById(data.items, itemIdToExpand));
            const groupedPageInvalid = isGroupedFormPageInvalidForEntity(data, itemIdToExpand, previousTocItemState, entity?.id);

            if (itemMissing || groupedPageInvalid) {
                itemIdToExpand = findItemIdToExpand(data.defaultItem?.id, referenceMap);
            }
        }
        if (itemIdToExpand) {
            expandTocNodePath(itemIdToExpand, data.items);
        }

        const formPageNameFromUrl: string | null = searchParams.get(TITLE_SEARCH_PARAM_NAME);
        const expandedItemFromId: TaxReturnTOCItem | undefined = formPageNameFromUrl
            ? findTocItemByIdAndFormPageName(data.items, itemIdToExpand, formPageNameFromUrl) ?? findTocItemById(data.items, itemIdToExpand)
            : findTocItemById(data.items, itemIdToExpand);
        const expandedItemKey: TaxReturnTOCKey | undefined = getTocItemKey(expandedItemFromId);

        setTocData(data);
        setEmptyDetailedWorkspaceItemsMap(buildEmptyDetailedWorkspaceItemsMap(data.itemsToAdd));

        const updatedCurrentTOCItemState = getUpdatedCurrentTOCItemStateOnTree(data, referenceMap, expandedItemKey, itemIdToExpand, entity);
        if (updatedCurrentTOCItemState && updatedCurrentTOCItemState.id.length === 0) {
            setHasError(true);
            handleError();
        }

        setCurrentTOCItemState(updatedCurrentTOCItemState);

        if (skipGetFormData) return;

        const parentTocItemKey: TaxReturnTOCKey | undefined = expandedItemFromId?.parentId
            ? getTocItemKey(findTocItemById(data.items, expandedItemFromId.parentId))
            : undefined;
        const { formPages } = getTocItemPagesAndParentId(referenceMap, expandedItemKey, parentTocItemKey);
        if (formPages) {
            setCurrentFormTotalPages(formPages.length);

            // Check if overflow statement is already being printed before loading
            // Only block overflow statements - normal statements can load from print queue
            const tocItemKeyToExpand: TaxReturnTOCKey | undefined = getTocKeyByItemId(referenceMap, itemIdToExpand);
            const isOverflowBeingPrinted: boolean =
                Boolean(itemIdToExpand) && isTocItemPrinting(returnKey, tocItemKeyToExpand) && isOverflowStatement(formPages);

            if (!isOverflowBeingPrinted) {
                const formLoadGeneration: number = options?.formLoadGeneration ?? ++activeNavigationGenerationRef.current;

                if (!options?.formLoadGeneration) {
                    cancelViewForm();
                }

                pendingFormLoadGenerationRef.current = formLoadGeneration;
                setPdfViewerLoadedTocItemId(null);
                setPdfSyncPhase('idle');

                getBlobData(formPages, entity, {
                    selectedTocItemId: itemIdToExpand,
                    selectionTocData: data,
                    selectionReferenceMap: referenceMap,
                    isStale: () => formLoadGeneration !== activeNavigationGenerationRef.current,
                });
            } else {
                // Overflow statement is being printed, clear form data to show the message
                clearActiveMessage();
                setFormData(null);
                setFormDataType(null);
                setFormFields(null);
                setDetailedWorkspace(null);
            }
        }
    };

    const { mutateAsync: getTaxReturnTOC, isLoading: isLoadingToc } = useGetTaxReturnTOC();

    const { mutateAsync: getTaxReturnTOCForEntity, isLoading: isLoadingEntityToc } = useGetTaxReturnTOC({
        mutation: {
            onError: (error: Error) => {
                handleError(error.message);
            },
        },
    });

    const focusElementAfterFormModal = () => {
        if (elementToFocusAfterFormModal) {
            // Wait for DOM to update after TOC reload, then try to focus
            setTimeout(() => {
                const elementToFocus = document.getElementById(elementToFocusAfterFormModal);
                if (elementToFocus) {
                    elementToFocus.focus();
                } else {
                    // Fallback: focus the Statements folder in TOC
                    const statementsElement = Array.from(document.querySelectorAll('[role="treeitem"]')).find((el) =>
                        el.textContent?.trim().toLowerCase().includes('statements')
                    );

                    if (statementsElement) {
                        (statementsElement as HTMLElement).focus();
                    }
                }
            }, 200);
        }
    };

    const updateCurrentPage = (item: TaxReturnTOCItem, entity?: ISelectedEntity) => {
        const pathEntity = getSelectedEntityFromPath(searchParams);
        const tocKey: TaxReturnTOCKey | undefined = getTocItemKey(item);

        const updatedPage: CurrentTOCItemState = {
            ...currentTOCItemState,
            id: item.id || '',
            pageIndex: resolveTocItemPageIndex(item, tocData?.items),
            parentId: item.parentId,
            tocKey: tocKey ?? null,
            tocItemTitle: item.title,
            isEnumerated: item.isEnumerated || false,
            canSelectEntity: item.canSelectEntity ?? true,
            dataName: item.dataName,
            taxReturnItemType: item.taxReturnTOCKey?.taxReturnItemType,
        };

        if (entity || pathEntity) updatedPage.entity = entity || pathEntity;
        setCurrentTOCItemState(updatedPage);
    };

    const expandTocNodePath = (tocItemId: string, tocItems?: TaxReturnTOCItem[] | null | undefined): void => {
        const resolvedTocItems: TaxReturnTOCItem[] | null | undefined = filterTOCItemsByVisibility(tocItems ?? tocData?.items);
        const parentIds: string[] = collectParentTocItemIds(resolvedTocItems, tocItemId);
        const nodeIds: string[] = [...parentIds, tocItemId];

        if (nodeIds.length === 0) {
            return;
        }

        dispatchTocTreeState({
            type: FormTocViewActionType.ExpandNodes,
            nodeIds,
        });
    };

    const setTocNodeExpanded = (nodeId: string, isExpanded: boolean): void => {
        dispatchTocTreeState({
            type: FormTocViewActionType.ToggleNode,
            nodeId,
            isExpanded,
        });
    };

    const expandAllTocNodes = (): void => {
        const allNodeIds: string[] = collectTocItemIds(tocData?.items);

        dispatchTocTreeState({
            type: FormTocViewActionType.ExpandAll,
            allNodeIds,
        });
    };

    const collapseAllTocNodes = (): void => {
        dispatchTocTreeState({
            type: FormTocViewActionType.CollapseAll,
        });
    };

    const setNodesExpanded = (
        expanded: boolean,
        node?: TaxReturnTOCItem | undefined,
        _tocReferenceMap?: Map<TaxReturnTOCKey, TaxReturnTOCItem>
    ): void => {
        if (node?.id) {
            setTocNodeExpanded(node.id, expanded);
            return;
        }

        if (expanded) {
            expandAllTocNodes();
        } else {
            collapseAllTocNodes();
        }
    };

    const getFormRequestData = (taxReturnTOCItems: TaxReturnTOCItem[], entity?: ISelectedEntity, taxReturnItemType?: TaxReturnItemType) => {
        const requestData: TaxReturnItemRequest = {
            returnKey: returnKey,
            returnId: currentReport?.taxReturnItemId ?? '',
            returnName: currentReport?.name ?? '',
            taxReturnTOCItems,
            taxReturnItemType,
            suppressEditableFieldFill: SUPPRESS_EDITABLE_FIELD_FILL,
            entityCode: currentReport?.entityCode ?? undefined,
        };

        if (SUPPRESS_EDITABLE_FIELD_FILL) {
            // The server authors the interactive (drill-down/descriptive) light-blue borders,
            // evaluating the same rule the viewer used against the report the viewer has open —
            // so send it the open report's row sets. Empty sets (report not loaded, or the
            // feature flag off) author no interactive borders, matching the old behavior of
            // an empty drilldown context.
            requestData.whitePaperRowNumbers = isFormsViewDrilldownEnabled
                ? (currentWhitePaperReport?.rows ?? [])
                      .map((row) => row.rowNumber)
                      .filter((rowNumber): rowNumber is number => typeof rowNumber === 'number')
                : [];
            requestData.descriptiveDataRowNumbers = isEditFormViewDescriptiveDataEnabled
                ? Object.keys(currentWhitePaperReport?.legacyDescriptiveDataFields ?? {})
                      .map(Number)
                      .filter((rowNumber) => Number.isInteger(rowNumber))
                : [];
            requestData.whitePaperColumnCount = currentWhitePaperReport?.columns?.length ?? 0;
        }

        if (entity) {
            // The whitepaper report remaps column ids. columnId carries the remapped id the
            // viewer has always sent; originalColumnId additionally carries the calc engine's
            // real column id (e.g. 99991 for Total) from the report columns metadata when the
            // entity was resolved against them. Only the server knows whether the return is an
            // enhanced or legacy calculation, so it decides which id the form pipeline needs
            // (field override locks and tickmark matching key off the original id on enhanced
            // returns — which is what SMART sends).
            const entityColumn =
                currentWhitePaperReport?.columns?.find((column) => column.columnId === entity.id) ??
                currentWhitePaperReport?.columns?.[entity.index];
            requestData.columnId = entity.id;
            requestData.originalColumnId = entity.originalColumnId ?? entityColumn?.originalColumnId;
            requestData.columnIndex = entity.index;
        }

        return requestData;
    };

    const navigateToTocItem = (target: NavigationTarget, entity?: ISelectedEntity | undefined): void => {
        const selectedItemFromKey = getTocItemFromKeyMap(tocReferenceMap, target.selectedKey);
        const selectedTocKey: TaxReturnTOCKey | undefined = target.selectedKey;

        if (isTocItemPrinting(returnKey, selectedTocKey) && isOverflowStatement(target.itemsToLoad)) {
            setHasError(false);
            setHasAbortedOrTimedOutError(false);
            clearActiveMessage();

            if (tocData && selectedItemFromKey?.id) {
                expandTocNodePath(selectedItemFromKey.id, tocData?.items);
            }

            const currentItem = getUpdatedCurrentTOCItemStateOnTree(
                tocData,
                tocReferenceMap,
                selectedTocKey,
                target.selectedItemId,
                entity
            );
            setCurrentTOCItemState(currentItem);
            return;
        }

        const isSameLoadedIsolatedReNavigation: boolean = isSameLoadedIsolatedItemReNavigation(
            target,
            currentTOCItemState,
            selectedItemFromKey,
            tocData?.items,
            Boolean(formData) && !isLoadingPdf
        );

        if (isSameLoadedIsolatedReNavigation) {
            if (tocData && target.selectedItemId) {
                expandTocNodePath(target.selectedItemId, tocData?.items);
            }
            return;
        }

        const navigationGeneration = ++activeNavigationGenerationRef.current;
        cancelViewForm();

        setHasError(false);
        setHasAbortedOrTimedOutError(false);
        entity = entity || (currentTOCItemState ? currentTOCItemState.entity : undefined);

        if (tocData && target.selectedItemId) {
            expandTocNodePath(target.selectedItemId, tocData.items);
        }

        const isIntraGroupedPdfNavigation = isIntraPdfNavigation(
            selectedItemFromKey,
            currentTOCItemState,
            tocData?.items,
            pdfViewerLoadedTocItemIdRef.current,
            pdfSyncPhaseRef.current
        );

        requestPdfNavigation();

        if (isIntraGroupedPdfNavigation) {
            setPdfSyncPhase('syncing-page');
        } else {
            setPdfViewerLoadedTocItemId(null);
            setPdfSyncPhase('idle');
        }

        const currentItem = getUpdatedCurrentTOCItemStateOnTree(tocData, tocReferenceMap, selectedTocKey, target.selectedItemId, entity);

        if (!currentItem) {
            setHasError(true);
            handleError();
            return;
        }

        const nextItemState: CurrentTOCItemState = {
            ...currentItem,
            pageIndex: target.pageIndex,
        };

        setCurrentTOCItemState(nextItemState);

        if (isIntraGroupedPdfNavigation) {
            if (selectedItemFromKey?.id) {
                setPdfViewerLoadedTocItemId(selectedItemFromKey.id);
            }
            return;
        }

        pendingFormLoadGenerationRef.current = navigationGeneration;

        getBlobData(target.itemsToLoad, entity, {
            selectedTocItemId: target.selectedItemId,
            selectionTocData: tocData,
            selectionReferenceMap: tocReferenceMap,
            isStale: () => navigationGeneration !== activeNavigationGenerationRef.current,
        }).then(() => {
            if (navigationGeneration !== activeNavigationGenerationRef.current) {
                return;
            }

            if (target.kind === 'grouped-page') {
                setCurrentFormTotalPages(target.itemsToLoad.length);
            }
        });
    };

    const loadFormPage = (taxReturnTocItems: TaxReturnTOCItem[], tocKey: TaxReturnTOCKey, entity?: ISelectedEntity | undefined) => {
        const selectedItemFromKey = getTocItemFromKeyMap(tocReferenceMap, tocKey);
        const selectedItemId = selectedItemFromKey?.id ?? taxReturnTocItems.find((item) => item.taxReturnTOCKey === tocKey)?.id ?? '';

        if (!selectedItemId) {
            return;
        }

        const pageIndex = selectedItemFromKey ? resolveTocItemPageIndex(selectedItemFromKey, tocData?.items) : 0;

        const kind = selectedItemFromKey && isIsolatedTocItem(selectedItemFromKey, tocData?.items) ? 'isolated-item' : 'grouped-page';

        navigateToTocItem(
            {
                itemsToLoad: taxReturnTocItems,
                selectedKey: tocKey,
                selectedItemId,
                pageIndex,
                kind,
            },
            entity
        );
    };

    const navigateToTocItemFromTree = (item: TaxReturnTOCItem, entity?: ISelectedEntity | undefined): void => {
        const target = resolveNavigationTarget(item, tocData);
        if (target) {
            navigateToTocItem(target, entity);
        }
    };

    const navigateToSectionForm = (sectionIndex: number, options?: { goToLastPage?: boolean }): void => {
        const navigationItems = buildSectionNavigationList(tocData, currentTOCItemState);
        const target = resolveNavigationTargetBySectionIndex(navigationItems, sectionIndex, tocData, options);
        if (target) {
            navigateToTocItem(target);
        }
    };

    const getCurrentTocItem = (): TaxReturnTOCItem | undefined => {
        const tocItemState = currentTOCItemStateRef.current;
        if (!tocItemState?.id) {
            return undefined;
        }

        return (
            (tocItemState.tocKey ? getTocItemFromKeyMap(tocReferenceMap, tocItemState.tocKey) : undefined) ??
            findTocItemById(tocData?.items, tocItemState.id)
        );
    };

    const navigateToPageNumber = (pageNumber: number): void => {
        if (pageNumber < 1) {
            return;
        }

        const tocItemState = currentTOCItemStateRef.current;
        if (!tocItemState?.id) {
            return;
        }

        const currentItem = getCurrentTocItem();
        if (!currentItem) {
            return;
        }

        const pageIndex = pageNumber - 1;

        if (isIsolatedTocItem(currentItem, tocData?.items)) {
            requestPdfNavigation();
            setPdfSyncPhase('syncing-page');
            setCurrentTOCItemState((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    pageIndex,
                };
            });
            return;
        }

        const parentElement: TaxReturnTOCItem | undefined = findTocItemById(tocData?.items, tocItemState.parentId);
        const visibleChildren = getVisibleChildren(parentElement);
        if (pageIndex < 0 || pageIndex >= visibleChildren.length) {
            return;
        }

        const selectedPage = visibleChildren[pageIndex];
        if (!selectedPage?.taxReturnTOCKey || !selectedPage.id) {
            return;
        }

        navigateToTocItem({
            itemsToLoad: visibleChildren,
            selectedKey: selectedPage.taxReturnTOCKey,
            selectedItemId: selectedPage.id,
            pageIndex,
            kind: 'grouped-page',
        });
    };

    const navigateToRelativePageNumber = (delta: number): void => {
        const currentPageNumber = (currentTOCItemStateRef.current?.pageIndex ?? 0) + 1;
        navigateToPageNumber(currentPageNumber + delta);
    };

    const navigateToFirstPage = (): void => {
        navigateToPageNumber(1);
    };

    const navigateToLastPage = (): void => {
        navigateToPageNumber(currentFormTotalPagesRef.current);
    };

    const handeSuccessLoadTocData = (data: TaxReturnTOC, options?: LoadTocDataOptions): void => {
        const baseTocData: TaxReturnTOC = cloneDeep(data);
        const skipGetFormData: boolean = options?.skipGetFormData ?? false;
        const resolvedEntity: ISelectedEntity | undefined = getResolvedSelectedEntity(
            options?.entity,
            selectedEntity,
            currentTOCItemState,
            searchParams,
            currentWhitePaperReport
        );
        const tocItems: TaxReturnTOCItem[] | null | undefined = baseTocData.items;
        const { tocReferenceMap: newTocReferenceMap, dataNameToTocItemMap } = buildTocMaps(tocItems ?? []);
        setTocReferenceMap(newTocReferenceMap);
        setDataNameToTocItemMap(dataNameToTocItemMap);
        setEmptyDetailedWorkspaceItemsMap(buildEmptyDetailedWorkspaceItemsMap(baseTocData.itemsToAdd));
        setSelectedEntity(resolvedEntity);
        if (tocItems && tocItems.length > 0) {
            setHasTaxReturnTOCItemsTrue();
        }

        const shouldPreserveExpanded: boolean = Boolean(currentTOCItemState?.id && isTocItemVisible(tocItems, currentTOCItemState.id));
        initializeTocTreeState(tocItems, shouldPreserveExpanded);

        const itemIdToExpand: string | undefined = resolveItemIdToExpand(baseTocData, newTocReferenceMap);
        initializeTocState({ ...baseTocData, items: tocItems }, newTocReferenceMap, itemIdToExpand, resolvedEntity, skipGetFormData, {
            formLoadGeneration: options?.formLoadGeneration,
        });

        focusElementAfterFormModal();
    };

    const loadTocData = (columnIndex?: number, options?: LoadTocDataOptions): void => {
        setHasError(false);
        setHasAbortedOrTimedOutError(false);
        setHasTaxReturnTOCItemsFalse();
        const resolvedEntity: ISelectedEntity | undefined = getResolvedSelectedEntity(
            options?.entity,
            selectedEntity,
            currentTOCItemState,
            searchParams,
            currentWhitePaperReport
        );
        const resolvedColumnIndex: number = columnIndex ?? resolvedEntity?.index ?? currentTOCItemState?.entity?.index ?? 0;
        const resolvedEntityId: number | undefined = resolvedEntity?.id;
        const resolvedEntityCode: string | undefined = resolvedEntity?.code;

        getTaxReturnTOC({
            data: {
                taxReturnItemTOCKey: returnKey,
                columnIndex: resolvedColumnIndex,
                entityId: resolvedEntityId,
                entityCode: resolvedEntityCode,
            },
        })
            .then((data: TaxReturnTOC) => {
                handeSuccessLoadTocData(data, {
                    skipGetFormData: options?.skipGetFormData,
                    entity: resolvedEntity,
                });
            })
            .catch((error: Error) => {
                handleError(error.message);
            });
    };

    const loadTocAndFormForEntity = async (entity: IWhitePaperColumn | undefined): Promise<void> => {
        setHasError(false);
        setHasAbortedOrTimedOutError(false);

        const selectedEntity: ISelectedEntity | undefined = getSelectedEntityFromColumn(entity);

        if (selectedEntity) {
            setTocSearchText('');
            const entitySwitchGeneration: number = ++activeNavigationGenerationRef.current;
            cancelViewForm();
            setSelectedEntity(selectedEntity);
            setFormData(null);
            setFormDataType(null);
            setFormFields(null);
            setDetailedWorkspace(null);
            setPdfViewerLoadedTocItemId(null);
            setPdfSyncPhase('idle');
            setCurrentTOCItemState((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    entity: selectedEntity,
                    id: prev.id || '',
                    canSelectEntity: prev.canSelectEntity ?? true,
                };
            });
            const currentTocItems: TaxReturnTOCItem[] | null | undefined = tocData?.items;
            const currentReturnType: PassthruType | undefined = tocData?.returnType;
            if (tocContainsEntityCodes(currentTocItems) && currentReturnType === PassthruType.Partnership) {
                // Get a new TOC from API to get the correct list of emptyItems
                const newTocData: TaxReturnTOC = await getTaxReturnTOCForEntity({
                    data: {
                        taxReturnItemTOCKey: returnKey,
                        columnIndex: selectedEntity.index,
                        entityId: selectedEntity.id,
                        entityCode: selectedEntity.code,
                    },
                });
                const tocItems: TaxReturnTOCItem[] | null | undefined = newTocData?.items;
                const { tocReferenceMap: newTocReferenceMap, dataNameToTocItemMap: newDataNameToTocItemMap } = buildTocMaps(tocItems ?? []);
                setTocReferenceMap(newTocReferenceMap);
                setDataNameToTocItemMap(newDataNameToTocItemMap);

                if (tocItems && tocItems.length > 0) {
                    setHasTaxReturnTOCItemsTrue();
                }

                const shouldPreserveExpanded: boolean = Boolean(
                    currentTOCItemState?.id && isTocItemVisible(tocItems, currentTOCItemState.id)
                );
                initializeTocTreeState(tocItems, shouldPreserveExpanded);

                const updatedTocData: TaxReturnTOC = { ...newTocData, items: tocItems };
                const itemIdToExpand: string | undefined = resolveItemIdToExpand(updatedTocData, newTocReferenceMap);

                initializeTocState(updatedTocData, newTocReferenceMap, itemIdToExpand, selectedEntity, false, {
                    formLoadGeneration: entitySwitchGeneration,
                });
            } else {
                // Support old behavior for versions < 2026 with no entityCode in the TOC data or return type is not Partnership
                loadTocData(selectedEntity.index, {
                    entity: selectedEntity,
                    formLoadGeneration: entitySwitchGeneration,
                });
            }
        }
    };

    const onPageChange = (page: any) => {
        const phase = pdfSyncPhaseRef.current;
        if (phase !== 'ready' && phase !== 'syncing-page') {
            return;
        }

        const tocItemState = currentTOCItemStateRef.current;
        if (!tocItemState?.id) {
            return;
        }

        const currentItem: TaxReturnTOCItem | undefined =
            (tocItemState.tocKey ? getTocItemFromKeyMap(tocReferenceMap, tocItemState.tocKey) : undefined) ??
            findTocItemById(tocData?.items, tocItemState.id);

        if (!currentItem || isIsolatedTocItem(currentItem, tocData?.items)) {
            setCurrentTOCItemState((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    pageIndex: page.currentPageNumber - 1,
                };
            });
            return;
        }

        const parentElement: TaxReturnTOCItem | undefined = findTocItemById(tocData?.items, tocItemState.parentId);
        if (!parentElement?.children || parentElement.children.length === 0) {
            return;
        }

        const childIndex = page.currentPageNumber - 1;
        if (childIndex < 0 || childIndex >= parentElement.children.length) {
            return;
        }

        const selectedPage = parentElement.children[childIndex];

        if (selectedPage && selectedPage.id) {
            if (tocData) {
                expandTocNodePath(selectedPage.id, tocData.items);
            }

            const selectedPageKey: TaxReturnTOCKey | undefined = getTocItemKey(selectedPage);

            setCurrentTOCItemState(
                getUpdatedCurrentTOCItemStateOnTree(tocData, tocReferenceMap, selectedPageKey, selectedPage.id, tocItemState?.entity)
            );
        }
    };

    const togglePinDrawer = () => {
        if (isDrawerPinned) {
            searchParams.delete(PINNED_SEARCH_PARAM_NAME);
        } else {
            searchParams.set(PINNED_SEARCH_PARAM_NAME, 'true');
        }

        navigate({ ...location, search: searchParams.toString() }, { replace: true });
    };

    const setTocSearchText = (nextSearchText: string, options?: { syncUrl?: boolean }): void => {
        const trimmedSearchText: string = nextSearchText.trim();
        const shouldSyncUrl: boolean = options?.syncUrl !== false;

        const allNodeIds: string[] = collectTocItemIds(tocData?.items);
        const shouldExpandAll: boolean = trimmedSearchText.length > 0 && tocTreeState.searchText.length === 0;

        dispatchTocTreeState({
            type: FormTocViewActionType.SearchChanged,
            searchText: trimmedSearchText,
            allNodeIds,
            shouldExpandAll,
        });

        if (!shouldSyncUrl) {
            return;
        }

        const formRouteRegex = new RegExp(`^${VIEW_REPORT_URL}\\d+/forms`);
        if (!formRouteRegex.test(location.pathname)) {
            return;
        }

        const nextSearchParams = new URLSearchParams(location.search);
        if (trimmedSearchText.length > 0) {
            nextSearchParams.set(TOC_SEARCH_PARAM_NAME, trimmedSearchText);
        } else {
            if (!nextSearchParams.has(TOC_SEARCH_PARAM_NAME)) {
                return;
            }
            nextSearchParams.delete(TOC_SEARCH_PARAM_NAME);
        }

        navigate({ pathname: location.pathname, search: nextSearchParams.toString() }, { replace: true });
    };

    const setFormSetting = (key: string, value: any) => {
        setFormSettings((previousSettings) => ({ ...previousSettings, [key]: value }));
    };

    const updateFormSettings = (settings: Partial<FormSettings>) => {
        setFormSettings((previousSettings) => ({ ...previousSettings, ...settings }));
    };

    const updateCurrentFormTotalPages = (totalPages: number) => {
        setCurrentFormTotalPages(totalPages);
    };

    return (
        <FormViewContext.Provider
            value={
                value ?? {
                    showForm,
                    formData,
                    tocData,
                    formDataType,
                    isLoading: isLoadingPdf || isLoadingToc || isLoadingEntityToc,
                    isLoadingToc,
                    isLoadingDetailedWorkspace,
                    isFormZoomReady,
                    setFormZoomReady: setIsFormZoomReady,
                    loadTocData,
                    loadFormPage,
                    navigateToTocItem,
                    navigateToTocItemFromTree,
                    navigateToSectionForm,
                    navigateToPageNumber,
                    navigateToRelativePageNumber,
                    navigateToFirstPage,
                    navigateToLastPage,
                    pdfSyncPhase,
                    setPdfSyncPhase,
                    pdfNavigationRequestId,
                    openForm,
                    navigateToReport,
                    clearFormData,
                    currentTOCItemState,
                    selectedStatement,
                    setSelectedStatement,
                    onPageChange,
                    currentFormTotalPages,
                    pdfViewerLoadedTocItemId,
                    setPdfViewerLoadedTocItemId,
                    isDrawerPinned,
                    togglePinDrawer,
                    hasTaxReturnTOCItems: hasTaxReturnTOCItems,
                    formSettings,
                    setFormSetting,
                    updateFormSettings,
                    setNodesExpanded,
                    tocTreeState,
                    setTocSearchText,
                    expandAllTocNodes,
                    collapseAllTocNodes,
                    setTocNodeExpanded,
                    getCurrentFormPageUrl,
                    setCurrentTOCItemState,
                    formFields,
                    detailedWorkspace,
                    selectedEntity,
                    returnKey: returnKey,
                    currentReport,
                    setCurrentReport,
                    currentWhitePaperReport,
                    setCurrentWhitePaperReport,
                    commentMetadataVersion,
                    setCommentMetadataVersion,
                    updateCurrentFormTotalPages,
                    hasError,
                    hasAbortedOrTimedOutError,
                    cancelCurrentFormLoad,
                    pendingOverflowPrintSelection,
                    requestOverflowPrint,
                    clearOverflowPrintRequest,
                    isTocItemPrinting,
                    loadTocAndFormForEntity,
                    tocReferenceMap,
                    dataNameToTocItemMap,
                    emptyDetailedWorkspaceItemsMap,
                    showStatement,
                    setShowStatement,
                    loadStatementDataForModal,
                    setDataNameToTocItemMap,
                    elementToFocusAfterFormModal,
                    setElementToFocusAfterFormModal,
                    openAddStatementModal,
                }
            }
        >
            {children}
        </FormViewContext.Provider>
    );
}

export default FormViewProvider;
